import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HeroCarousel = () => {
  const [api, setApi] = useState();
  const [current, setCurrent] = useState(0);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublishedStories();
  }, []);

  const fetchPublishedStories = async () => {
    try {
      const response = await axios.get(`${API}/stories/?status=Published`);
      // Get only the first 5 stories for the carousel
      setStories(response.data.data?.slice(0, 5) || []);
    } catch (err) {
      console.error('Error fetching published stories:', err);
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });

    // Auto-play carousel
    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [api]);

  if (loading) {
    return (
      <div className="relative w-full h-[500px] sm:h-[600px] flex items-center justify-center bg-accent/50 rounded-2xl">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div className="relative w-full h-[500px] sm:h-[600px] flex items-center justify-center bg-accent/50 rounded-2xl">
        <div className="text-center">
          <p className="text-muted-foreground text-lg">No published stories yet</p>
          <p className="text-sm text-muted-foreground mt-2">Check back soon for inspiring startup stories!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <Carousel
        setApi={setApi}
        className="w-full"
        opts={{
          align: 'start',
          loop: true,
        }}
      >
        <CarouselContent>
          {stories.map((story) => (
            <CarouselItem key={story.id}>
              <div className="relative h-[500px] sm:h-[600px] overflow-hidden rounded-2xl">
                {/* Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: story.banner_photo 
                      ? `url(${story.banner_photo})` 
                      : `url(https://images.unsplash.com/photo-1600880292089-90a7e086ee0c)`,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-transparent" />
                </div>

                {/* Content */}
                <div className="relative h-full container mx-auto px-6 sm:px-8 lg:px-12 py-12 flex items-center">
                  <div className="max-w-2xl space-y-4">
                    <Badge variant="category" className="text-xs">
                      {story.submission_type || 'Startup Story'}
                    </Badge>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading text-white leading-tight">
                      {story.title || story.startup_name}
                    </h1>
                    <p className="text-base sm:text-lg text-white/90 leading-relaxed line-clamp-2">
                      {story.short_description || story.brief_info?.substring(0, 120) + '...'}
                    </p>
                    <div className="flex items-center gap-4 pt-2">
                      <Button 
                        variant="default" 
                        size="lg" 
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => window.location.href = `/story/${story.slug}`}
                      >
                        Read Story
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                      <span className="text-white/80 text-sm">
                        {story.startup_name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="hidden md:block">
          <CarouselPrevious className="left-4" />
          <CarouselNext className="right-4" />
        </div>
      </Carousel>

      {/* Carousel Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
        {stories.map((_, index) => (
          <button
            key={index}
            className={`h-2 rounded-full transition-all ${
              current === index ? 'w-8 bg-primary' : 'w-2 bg-white/50 hover:bg-white/70'
            }`}
            onClick={() => api?.scrollTo(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
